import { createContext, useContext, useState, useEffect, useCallback } from 'react'

export type DensityPreset = 'comfortable' | 'compact' | 'dense' | 'custom'

export interface UIPreferences {
  density: DensityPreset
  /** Base font size in px (default 14) */
  fontSize: number
  /** Base spacing unit in px (default 16) — controls padding/margin/gap scale */
  spacingUnit: number
  /** Line height multiplier (default 1.5) */
  lineHeight: number
  /** Border radius in px (default 12) */
  borderRadius: number
}

const PRESETS: Record<Exclude<DensityPreset, 'custom'>, Omit<UIPreferences, 'density'>> = {
  comfortable: { fontSize: 14, spacingUnit: 16, lineHeight: 1.5, borderRadius: 12 },
  compact:     { fontSize: 13, spacingUnit: 12, lineHeight: 1.35, borderRadius: 8 },
  dense:       { fontSize: 12, spacingUnit: 8, lineHeight: 1.2, borderRadius: 6 },
}

const STORAGE_KEY = 'iflow_ui_preferences'

const defaultPrefs: UIPreferences = { density: 'comfortable', ...PRESETS.comfortable }

function loadPrefs(): UIPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return { ...defaultPrefs, ...parsed }
    }
  } catch {}
  return defaultPrefs
}

function applyCSS(prefs: UIPreferences) {
  const root = document.documentElement
  root.style.setProperty('--ui-font-size', `${prefs.fontSize}px`)
  root.style.setProperty('--ui-spacing', `${prefs.spacingUnit}px`)
  root.style.setProperty('--ui-spacing-xs', `${Math.round(prefs.spacingUnit * 0.25)}px`)
  root.style.setProperty('--ui-spacing-sm', `${Math.round(prefs.spacingUnit * 0.5)}px`)
  root.style.setProperty('--ui-spacing-md', `${prefs.spacingUnit}px`)
  root.style.setProperty('--ui-spacing-lg', `${Math.round(prefs.spacingUnit * 1.5)}px`)
  root.style.setProperty('--ui-spacing-xl', `${prefs.spacingUnit * 2}px`)
  root.style.setProperty('--ui-line-height', `${prefs.lineHeight}`)
  root.style.setProperty('--ui-radius', `${prefs.borderRadius}px`)
  root.style.setProperty('--ui-radius-sm', `${Math.round(prefs.borderRadius * 0.5)}px`)
  root.style.setProperty('--ui-radius-lg', `${Math.round(prefs.borderRadius * 1.5)}px`)

  // Scale factor for components that use proportional sizing
  const scale = prefs.spacingUnit / 16
  root.style.setProperty('--ui-scale', `${scale}`)
}

interface UIPreferencesContextType {
  prefs: UIPreferences
  setDensity: (preset: DensityPreset) => void
  setCustomValue: (key: keyof Omit<UIPreferences, 'density'>, value: number) => void
  presets: typeof PRESETS
}

const UIPreferencesContext = createContext<UIPreferencesContextType | null>(null)

export function UIPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<UIPreferences>(loadPrefs)

  useEffect(() => {
    applyCSS(prefs)
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)) } catch {}
  }, [prefs])

  const setDensity = useCallback((preset: DensityPreset) => {
    if (preset === 'custom') {
      setPrefs(prev => ({ ...prev, density: 'custom' }))
    } else {
      setPrefs({ density: preset, ...PRESETS[preset] })
    }
  }, [])

  const setCustomValue = useCallback((key: keyof Omit<UIPreferences, 'density'>, value: number) => {
    setPrefs(prev => ({ ...prev, density: 'custom' as DensityPreset, [key]: value }))
  }, [])

  return (
    <UIPreferencesContext.Provider value={{ prefs, setDensity, setCustomValue, presets: PRESETS }}>
      {children}
    </UIPreferencesContext.Provider>
  )
}

export function useUIPreferences() {
  const ctx = useContext(UIPreferencesContext)
  if (!ctx) throw new Error('useUIPreferences must be used within UIPreferencesProvider')
  return ctx
}
